import { InternalActionKeyId } from '@/define/internal_action_keys'
import { KeyMap, VK, type DefineDevice, VirtualND63Max } from '@hid/define/define'

const normalizeRoyuanKeyMap = (data: number[]): KeyMap => {
  return [data[0] & 0xff, data[1] & 0xff, data[2] & 0xff, data[3] & 0xff]
}

const royuanActionKeyMapByActionKey = new Map<InternalActionKeyId, KeyMap>([
  [InternalActionKeyId.deviceLightBrightnessUp, normalizeRoyuanKeyMap([0x0d, 0x02, 0x01, 0x00])],
  [InternalActionKeyId.deviceLightBrightnessDown, normalizeRoyuanKeyMap([0x0d, 0x02, 0x02, 0x00])],
  [InternalActionKeyId.deviceLightSpeedUp, normalizeRoyuanKeyMap([0x0d, 0x03, 0x01, 0x00])],
  [InternalActionKeyId.deviceLightSpeedDown, normalizeRoyuanKeyMap([0x0d, 0x03, 0x02, 0x00])],
  [InternalActionKeyId.deviceLightEffectMode, normalizeRoyuanKeyMap([0x0d, 0x01, 0x00, 0x00])],
  [InternalActionKeyId.deviceLightColorSwitch, normalizeRoyuanKeyMap([0x0d, 0x05, 0x01, 0x00])],
  [InternalActionKeyId.deviceLightToggle, normalizeRoyuanKeyMap([0x0a, 0x0c, 0x00, 0x00])],
  [InternalActionKeyId.deviceLightDirection, normalizeRoyuanKeyMap([0x0d, 0x05, 0x00, 0x00])],
  [InternalActionKeyId.deviceSideLightBrightnessUp, normalizeRoyuanKeyMap([0x0d, 0x08, 0x01, 0x00])],
  [InternalActionKeyId.deviceSideLightSpeedUp, normalizeRoyuanKeyMap([0x0d, 0x08, 0x02, 0x01])],
  [InternalActionKeyId.deviceSideLightSpeedDown, normalizeRoyuanKeyMap([0x0d, 0x08, 0x02, 0x02])],
  [InternalActionKeyId.deviceSideLightEffectMode, normalizeRoyuanKeyMap([0x0d, 0x08, 0x00, 0x00])],
  [InternalActionKeyId.deviceSideLightColorSwitch, normalizeRoyuanKeyMap([0x0d, 0x08, 0x03, 0x00])],
  [InternalActionKeyId.deviceSideLightDirection, normalizeRoyuanKeyMap([0x0d, 0x08, 0x04, 0x00])],
  [InternalActionKeyId.deviceFn1, normalizeRoyuanKeyMap([0x0a, 0x01, 0x00, 0x00])],
  [InternalActionKeyId.deviceFn2, normalizeRoyuanKeyMap([0x0a, 0x0b, 0x00, 0x00])],
  [InternalActionKeyId.deviceFn3, normalizeRoyuanKeyMap([0x0a, 0x0d, 0x00, 0x00])],
  [InternalActionKeyId.deviceRestore, normalizeRoyuanKeyMap([0x0a, 0x02, 0x00, 0x00])],
  [InternalActionKeyId.deviceWinKeyLock, normalizeRoyuanKeyMap([0x0a, 0x03, 0x00, 0x00])],
  [InternalActionKeyId.deviceSwitchToWindowsMode, normalizeRoyuanKeyMap([0x0a, 0x05, 0x00, 0x00])],
  [InternalActionKeyId.deviceSwitchToMacMode, normalizeRoyuanKeyMap([0x0a, 0x05, 0x01, 0x00])],
  [InternalActionKeyId.deviceWinMacModeSwitch, normalizeRoyuanKeyMap([0x0a, 0x05, 0x03, 0x00])],
  [InternalActionKeyId.deviceSwitchToProfile1, normalizeRoyuanKeyMap([0x08, 0x00, 0x04, 0x00])],
  [InternalActionKeyId.deviceSwitchToProfile2, normalizeRoyuanKeyMap([0x08, 0x00, 0x04, 0x01])],
  [InternalActionKeyId.deviceSwitchToProfile3, normalizeRoyuanKeyMap([0x08, 0x00, 0x04, 0x02])],
  [InternalActionKeyId.deviceSwitchToProfile4, normalizeRoyuanKeyMap([0x08, 0x00, 0x04, 0x03])],
])

const royuanHiddenDeviceFunctionActionKeys: ReadonlySet<InternalActionKeyId> = new Set([
  InternalActionKeyId.deviceFn2,
  InternalActionKeyId.deviceFn3,
  InternalActionKeyId.deviceSwitchToProfile1,
  InternalActionKeyId.deviceSwitchToProfile2,
  InternalActionKeyId.deviceSwitchToProfile3,
  InternalActionKeyId.deviceSwitchToProfile4,
])

export const royuanDeviceFunctionActionKeys: InternalActionKeyId[] = Array.from(royuanActionKeyMapByActionKey.keys()).filter((key) => !royuanHiddenDeviceFunctionActionKeys.has(key))

const royuanActionKeyMapOverridesByDevice = new Map<DefineDevice, ReadonlyMap<InternalActionKeyId, KeyMap>>([])

const royuanUnsupportedDeviceFunctionActionKeysByDevice = new Map<DefineDevice, ReadonlySet<InternalActionKeyId>>([])

const getRoyuanProfileActionKey = (profileIndex: number): InternalActionKeyId | undefined => {
  if (profileIndex === 0) return InternalActionKeyId.deviceSwitchToProfile1
  if (profileIndex === 1) return InternalActionKeyId.deviceSwitchToProfile2
  if (profileIndex === 2) return InternalActionKeyId.deviceSwitchToProfile3
  if (profileIndex === 3) return InternalActionKeyId.deviceSwitchToProfile4
  return undefined
}

const decodeRoyuanFnLedKey = (data: KeyMap): InternalActionKeyId | undefined => {
  const [byte0, byte1, byte2, byte3] = data

  if (byte0 !== 0x0d) {
    return undefined
  }

  if (byte1 === 0x00 && byte2 === 0x00 && byte3 === 0x00) {
    return InternalActionKeyId.deviceLightToggle
  }

  if (byte1 === 0x01) {
    return InternalActionKeyId.deviceLightEffectMode
  }

  if (byte1 === 0x02 && byte2 === 0x01) {
    return InternalActionKeyId.deviceLightBrightnessUp
  }

  if (byte1 === 0x02 && byte2 === 0x02) {
    return InternalActionKeyId.deviceLightBrightnessDown
  }

  if (byte1 === 0x03 && byte2 === 0x01) {
    return InternalActionKeyId.deviceLightSpeedUp
  }

  if (byte1 === 0x03 && byte2 === 0x02) {
    return InternalActionKeyId.deviceLightSpeedDown
  }

  if (byte1 === 0x05 && byte2 === 0x00) {
    return InternalActionKeyId.deviceLightDirection
  }

  if (byte1 === 0x05 && byte2 === 0x01) {
    return InternalActionKeyId.deviceLightColorSwitch
  }

  if (byte1 === 0x09 && byte2 === 0x00) {
    return InternalActionKeyId.deviceLightToggle
  }

  if (byte1 === 0x08 && byte2 === 0x00) {
    return InternalActionKeyId.deviceSideLightEffectMode
  }

  if (byte1 === 0x08 && byte2 === 0x01) {
    return InternalActionKeyId.deviceSideLightBrightnessUp
  }

  if (byte1 === 0x08 && byte2 === 0x02 && byte3 === 0x01) {
    return InternalActionKeyId.deviceSideLightSpeedUp
  }

  if (byte1 === 0x08 && byte2 === 0x02 && byte3 === 0x02) {
    return InternalActionKeyId.deviceSideLightSpeedDown
  }

  if (byte1 === 0x08 && byte2 === 0x03) {
    return InternalActionKeyId.deviceSideLightColorSwitch
  }

  if (byte1 === 0x08 && byte2 === 0x04) {
    return InternalActionKeyId.deviceSideLightDirection
  }

  return undefined
}

const decodeRoyuanFnSpecialKey = (data: KeyMap, deviceID: DefineDevice): InternalActionKeyId | undefined => {
  const [byte0, byte1, byte2, byte3] = data

  if (byte0 !== 0x0a || byte3 !== 0x00) {
    return undefined
  }

  if (byte1 === 0x01 && byte2 === 0x00) {
    return InternalActionKeyId.deviceFn1
  }

  if (byte1 === 0x02 && byte2 === 0x00) {
    return InternalActionKeyId.deviceRestore
  }

  if (byte1 === 0x03 && byte2 === 0x00) {
    return InternalActionKeyId.deviceWinKeyLock
  }

  if (byte1 === 0x05 && byte2 === 0x00) {
    return InternalActionKeyId.deviceSwitchToWindowsMode
  }

  if (byte1 === 0x05 && byte2 === 0x01) {
    return InternalActionKeyId.deviceSwitchToMacMode
  }

  if (byte1 === 0x05 && byte2 === 0x03) {
    return InternalActionKeyId.deviceWinMacModeSwitch
  }

  if (byte1 === 0x09 && byte2 === 0x00) {
    return InternalActionKeyId.deviceLightToggle
  }

  if (byte1 === 0x0b && byte2 === 0x00) {
    return InternalActionKeyId.deviceFn2
  }

  if (byte1 === 0x0c && byte2 === 0x00) {
    return InternalActionKeyId.deviceLightToggle
  }

  if (byte1 === 0x0d && byte2 === 0x00) {
    return InternalActionKeyId.deviceFn3
  }

  if (byte1 === 0x14 && byte2 === 0x00) {
    return InternalActionKeyId.deviceFn2
  }

  return undefined
}

export const getRoyuanFnActionKeyByKeyMap = (data: KeyMap, deviceID: DefineDevice): InternalActionKeyId | undefined => {
  const [byte0, byte1, byte2, byte3] = data

  const ledKey = decodeRoyuanFnLedKey(data)
  if (ledKey !== undefined) {
    return ledKey
  }

  const specialKey = decodeRoyuanFnSpecialKey(data, deviceID)
  if (specialKey !== undefined) {
    return specialKey
  }

  if (byte0 === 0x08 && byte1 === 0x00 && byte2 === 0x04) {
    return getRoyuanProfileActionKey(byte3)
  }

  if (byte0 !== 0x05) {
    return undefined
  }

  if (byte1 === 0x01 && byte3 === 0x00) {
    return getRoyuanProfileActionKey(byte2)
  }

  if (byte1 === 0x03 && byte3 === 0x01) {
    return InternalActionKeyId.deviceWinKeyLock
  }

  if (byte1 === 0x03 && byte3 === 0x02) {
    return InternalActionKeyId.deviceWinMacModeSwitch
  }

  if (byte1 === 0x03 && byte3 === 0x04) {
    return InternalActionKeyId.deviceLightToggle
  }

  if (byte1 === 0x04 && byte3 === 0x00) {
    return InternalActionKeyId.deviceLightEffectMode
  }

  if (byte1 === 0x0d && byte3 === 0x00) {
    return InternalActionKeyId.deviceRestore
  }

  return undefined
}

export const getRoyuanRawKeyMapByActionKey = (actionKey: number, deviceID: DefineDevice): KeyMap | undefined => {
  const overrideMap = royuanActionKeyMapOverridesByDevice.get(deviceID)
  const overrideValue = overrideMap?.get(actionKey as InternalActionKeyId)
  if (overrideValue !== undefined) {
    return overrideValue
  }

  return royuanActionKeyMapByActionKey.get(actionKey as InternalActionKeyId)
}

export const getRoyuanSupportedDeviceFunctionActionKeys = (deviceID: DefineDevice): InternalActionKeyId[] => {
  const unsupportedKeys = royuanUnsupportedDeviceFunctionActionKeysByDevice.get(deviceID)
  if (unsupportedKeys === undefined) {
    return [...royuanDeviceFunctionActionKeys]
  }

  return royuanDeviceFunctionActionKeys.filter((key) => !unsupportedKeys.has(key))
}

export const normalizeRoyuanFnKeyMap = normalizeRoyuanKeyMap
